import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Formik, Form } from 'formik';
import { RootState } from '../../store/config/types';
import * as actions from '../../store/actions/toast.actions';
import schemas from '../../utils/schemas';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import Button from '../../components/CustomButtons/Button';
import Card from '../../components/Card/Card';
import CardBody from '../../components/Card/CardBody';
import CardHeader from '../../components/Card/CardHeader';
import GoogleAutocomplete from '../../components/SpecialInput/GoogleAutocomplete/GoogleAutocomplete';
import InputField from '../../components/SpecialInput/InputField';
import MaskedPhoneInput from '../../components/MaskedPhoneInput/MaskedPhoneInput';
import Spinner from '../../components/Spinner/Spinner';
import { ShipperUser, ShipperUserGroups } from '../../store/config/types/shipperUser.types';
import SelectField from '../../components/SpecialInput/SelectField';
import { Grid, Link, MenuItem } from '@mui/material';
import { shippersService } from '../../services/shippers.service';
import ChangePasswordDialog from './ChangePasswordDialog';
import { logout } from '../../store/actions/auth.actions';
import { hasPermission } from '../../utils/permissions';

interface ShipperUserParams {
  shipperUserId?: string;
}

interface ShipperUserValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber: string;
  homeAddress: string;
  groupId: number;
}

const mapStateToProps = (state: RootState, props: { currentUser?: boolean }) => {
  return {
    auth: state.auth,
    currentUser: props.currentUser,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, any, any>) => ({
  setToast: (message: string, messageType: string) => dispatch(actions.setToast(message, messageType)),
  logout: () => dispatch(logout()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
const ShipperUserForm = ({ setToast, auth, currentUser, logout }: PropsFromRedux) => {
  const history = useHistory();
  const { shipperUserId } = useParams<ShipperUserParams>();
  const editMode: boolean = Boolean(shipperUserId || currentUser);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedShipperUser, setSelectedShipperUser] = useState<ShipperUser | null>(null);
  const [address, setAddress] = useState<string>('');
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const adminMode = !currentUser && hasPermission(auth.account, ['shipperAdmin.userManagement']);

  useEffect(() => {
    if (editMode && !selectedShipperUser) {
      const suId = shipperUserId ? +shipperUserId : currentUser ? auth?.account?.shipperUserId ?? 0 : 0;
      shippersService.getShipperUser(suId).then((response) => {
        if (response) {
          setSelectedShipperUser(response);
          setAddress(response.homeAddress ?? '');
        }
      });
    }
  }, [editMode, selectedShipperUser, shipperUserId]);

  useEffect(() => {
    // if (submitting) {
    //   setToast(`User successfully ${editMode ? 'updated' : 'created'}`, 'success');
    //   history.push('/user-control');
    //   setSubmitting(false);
    //   setToast(`An error has ocurred while ${editMode ? 'updating' : 'creating'} User`, 'danger');
    // }
  }, [submitting, history, setSubmitting, setToast]);

  const handleGoBack = () => history.push(currentUser ? '/' : '/user-control');

  const handleChangePasswordOpen = () => {
    setChangePasswordOpen(true);
  };
  const handleChangePasswordClose = (doLogout?: boolean) => {
    if (doLogout) {
      logout();
    }
    setChangePasswordOpen(false);
  };

  const submitForm = (values: ShipperUserValues) => {
    const shipperUserRequest: ShipperUser = {
      ...values,
    };

    setSubmitting(true);

    if (editMode) {
      shipperUserRequest.shipperUserId = selectedShipperUser?.shipperUserId;
      shippersService
        .updateShipperUser(shipperUserRequest)
        .then((response) => {
          if (response) {
            setToast(`User successfully updated`, 'success');
            history.push('/user-control');
          } else {
            setToast(`An error has ocurred while updating User`, 'danger');
          }
        })
        .catch(() => {
          setToast(`An error has ocurred while updating User`, 'danger');
        })
        .finally(() => {
          setSubmitting(false);
        });
      // onUpdateShipper(shipperRequest);
    } else {
      shippersService
        .createShipperUser(shipperUserRequest)
        .then((response) => {
          if (response) {
            setToast(`User successfully created`, 'success');
            history.push('/user-control');
          } else {
            setToast(`An error has ocurred while creating User`, 'danger');
          }
        })
        .catch(() => {
          setToast(`An error has ocurred while creating User`, 'danger');
        })
        .finally(() => {
          setSubmitting(false);
        });
      // onCreateShipper(shipperRequest);
    }
  };

  if (editMode && !selectedShipperUser) {
    return <Spinner />;
  }

  return (
    <div className="shipper-form">
      <Formik
        initialValues={{
          firstName: selectedShipperUser ? selectedShipperUser.firstName! : '',
          lastName: selectedShipperUser ? selectedShipperUser.lastName! : '',
          email: selectedShipperUser ? selectedShipperUser.email! : '',
          password: selectedShipperUser ? selectedShipperUser.password! : '',
          phoneNumber: selectedShipperUser ? selectedShipperUser.phoneNumber! : '',
          homeAddress: selectedShipperUser ? selectedShipperUser.homeAddress! : '',
          shipperId: selectedShipperUser ? selectedShipperUser.shipperUserId : shipperUserId,
          groupId: (selectedShipperUser?.groups ?? [{ groupId: 2 }])[0].groupId,
        }}
        validationSchema={schemas.ShipperUserSchema}
        onSubmit={submitForm}
      >
        {({ setFieldValue }) => (
          <Form>
            <Card profile>
              <CardHeader color="primary" className="card-header">
                {editMode && selectedShipperUser ? (
                  <h4>{`${selectedShipperUser.firstName} ${selectedShipperUser.lastName}`}</h4>
                ) : (
                  <h4>New User</h4>
                )}
              </CardHeader>
              <CardBody profile>
                <GridContainer justify="center">
                  <GridItem xs={12} sm={6}>
                    <InputField placeholder="First name" name="firstName" type="text" />
                  </GridItem>
                  <GridItem xs={12} sm={6}>
                    <InputField placeholder="Last name" name="lastName" type="text" />
                  </GridItem>
                </GridContainer>

                <Grid container alignItems="end">
                  <GridItem xs={12} sm={currentUser ? 8 : 4}>
                    <InputField placeholder="Email" name="email" type="text" disabled={editMode} />
                  </GridItem>
                  {editMode ? (
                    <GridItem xs={12} sm={4}>
                      <Link onClick={() => handleChangePasswordOpen()}>Change password</Link>
                    </GridItem>
                  ) : (
                    <GridItem xs={12} sm={4}>
                      <InputField placeholder="Password" name="password" type="password" />
                    </GridItem>
                  )}
                  {!currentUser && (
                    <GridItem xs={12} sm={4}>
                      <SelectField
                        placeholder="Type"
                        name="groupId"
                        leftAligned
                        disabled={`${auth.account?.shipperUserId}` === shipperUserId || currentUser}
                        values={ShipperUserGroups.map((group) => (
                          <MenuItem key={`shipper-user-group-${group.groupId}`} value={group.groupId}>
                            {group.name}
                          </MenuItem>
                        ))}
                      />
                    </GridItem>
                  )}
                </Grid>
                <GridContainer justify="center">
                  <GridItem xs={12} sm={8}>
                    <GoogleAutocomplete
                      address={address}
                      setAddress={setAddress}
                      setFieldValue={setFieldValue}
                      usesAllGoogleProps={true}
                      inputName="homeAddress"
                      placeholderName={'Home address'}
                      inputSize={12}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={4}>
                    <MaskedPhoneInput placeholder="Phone number" name="phoneNumber" setfieldvalue={setFieldValue} />
                  </GridItem>
                </GridContainer>
                <GridContainer>
                  <GridItem xs={12} sm={12}>
                    <div className="btn-group">
                      <Button type="submit" className="submit-button" color="primary">
                        {editMode ? 'UPDATE' : 'CREATE'} USER
                      </Button>
                      <Button onClick={handleGoBack} className="cancel-button" color="secondary">
                        Cancel
                      </Button>
                    </div>
                  </GridItem>
                </GridContainer>
              </CardBody>
            </Card>
          </Form>
        )}
      </Formik>

      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={handleChangePasswordClose}
        adminMode={adminMode && `${auth.account?.shipperUserId}` !== shipperUserId}
        user={selectedShipperUser}
      />
    </div>
  );
};

export default connector(ShipperUserForm);
